import { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";

export interface TimeValue {
  hours: number;
  minutes: number;
}

interface TimePickerProps {
  value?: TimeValue;
  defaultValue?: TimeValue;
  onChange?: (value: TimeValue) => void;
  use12Hour?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

function pad(n: number) { return String(n).padStart(2, "0"); }
function fmtDisplay(v: TimeValue, use12: boolean) {
  if (use12) {
    const h12 = v.hours % 12 || 12;
    const ampm = v.hours < 12 ? "AM" : "PM";
    return `${pad(h12)}:${pad(v.minutes)} ${ampm}`;
  }
  return `${pad(v.hours)}:${pad(v.minutes)}`;
}

export function TimePicker({
  value,
  defaultValue = { hours: 12, minutes: 0 },
  onChange,
  use12Hour = false,
  disabled,
  placeholder = "Select time",
  className,
}: TimePickerProps) {
  const controlled = value !== undefined;
  const [internal, setInternal] = useState<TimeValue>(defaultValue);
  const [open, setOpen] = useState(false);
  const [ampm, setAmpm] = useState<"AM" | "PM">("AM");
  const wrapRef = useRef<HTMLDivElement>(null);

  const current = controlled ? value! : internal;

  function update(next: TimeValue) {
    if (!controlled) setInternal(next);
    onChange?.(next);
  }

  // close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const displayHours = use12Hour ? (current.hours % 12 || 12) : current.hours;
  const maxH = use12Hour ? 12 : 23;

  function changeHours(delta: number) {
    let h = displayHours + delta;
    if (use12Hour) { h = ((h - 1 + 12) % 12) + 1; }
    else { h = (h + 24) % 24; }
    const realH = use12Hour ? (ampm === "AM" ? h % 12 : (h % 12) + 12) : h;
    update({ ...current, hours: realH });
  }
  function changeMinutes(delta: number) {
    const m = (current.minutes + delta + 60) % 60;
    update({ ...current, minutes: m });
  }
  function toggleAmPm(val: "AM" | "PM") {
    setAmpm(val);
    let h = current.hours % 12;
    update({ ...current, hours: val === "AM" ? h : h + 12 });
  }

  return (
    <div ref={wrapRef} className={cn("tp-wrap", className)}>
      <button
        type="button"
        className={cn("tp-trigger", open && "tp-trigger-open")}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="tp-icon" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        {open || current ? fmtDisplay(current, use12Hour) : placeholder}
      </button>
      {open && (
        <div className="tp-popover" role="dialog" aria-label="Time picker">
          <div className="tp-cols">
            {/* Hours */}
            <div className="tp-col">
              <button type="button" className="tp-arrow" onClick={() => changeHours(1)} aria-label="Increment hours">▲</button>
              <span className="tp-digit">{pad(displayHours)}</span>
              <button type="button" className="tp-arrow" onClick={() => changeHours(-1)} aria-label="Decrement hours">▼</button>
            </div>
            <span className="tp-sep">:</span>
            {/* Minutes */}
            <div className="tp-col">
              <button type="button" className="tp-arrow" onClick={() => changeMinutes(1)} aria-label="Increment minutes">▲</button>
              <span className="tp-digit">{pad(current.minutes)}</span>
              <button type="button" className="tp-arrow" onClick={() => changeMinutes(-1)} aria-label="Decrement minutes">▼</button>
            </div>
            {/* AM/PM */}
            {use12Hour && (
              <div className="tp-ampm">
                <button type="button" className={cn("tp-ampm-btn", ampm === "AM" && "tp-ampm-active")} onClick={() => toggleAmPm("AM")}>AM</button>
                <button type="button" className={cn("tp-ampm-btn", ampm === "PM" && "tp-ampm-active")} onClick={() => toggleAmPm("PM")}>PM</button>
              </div>
            )}
          </div>
          <div className="tp-footer">
            <button type="button" className="tp-done" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
